import type {
  Graph,
  QuestionNode as QuestionNodeType,
  PresetQuestion,
  Answer,
} from 'src/types/chatbot';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import {useSelector} from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import keyBy from 'lodash/keyBy';
import debounce from 'lodash/debounce';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

import nanoid from 'src/utils/id';

import {
  convertFromRaw,
  DraftDecoratorType,
  RawDraftContentState,
} from 'draft-js';

import {validateNode} from '../node-editor/validation';

import {
  useReleaseFlag,
  useChatbotLiveFlowEditEnabled,
} from 'src/hooks/product-flags';
import useI18n from 'src/hooks/useI18n';

import {useChatbotStateContext} from '../chatbot-state-context';
import {useOWBFields} from '../hooks';

import * as api from 'src/utils/api-no-store';
import {useMutableFieldStatus} from '../api-hooks';

import {
  createQuestionNode,
  createEditableGraph,
  findAncestorNodeVariables,
  getDerivedAddressFields,
  getDataTypeOptions,
  getChildEdges,
  writebackDataTypesToValidationTypes,
  QUESTION_SIMILARITY_THRESHOLD,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';

import classify from 'src/utils/classify';
import {hasText, createRawDraftContentState} from 'src/utils/draft';

import {useWritebackMetadataV2} from 'src/hooks/useWritebackAllowedAttributes';

import {Button} from 'src/designSystem2021Components/buttons';

import Loading from 'src/components/lib/loading';
import {SearchInput} from 'src/designSystem2021Components/search-input/search-input';
import CloseIcon from 'src/images/designSystems2021/close.svg';

import {MouseTip} from 'src/components/lib/mouse-tip/mouse-tip';
import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';
import LoadedVariablePicker from 'src/components/conversation-builder/node-editor/variable-picker';
import {Input} from 'src/designSystem2021Components/input';
import {Select} from 'src/designSystem2021Components/select';
import {Textarea} from 'src/designSystem2021Components/textarea';
import {Header} from 'src/designSystem2021Components/text-v2';
import {Checkbox} from 'src/designSystem2021Components/checkbox';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';

import ClearIcon from 'src/images/icons/clear-icon.svg';

import InfoIcon from 'src/images/icons/info-modal.svg';
import EditIcon from 'src/images/icons/edit-icon.svg';
import WarningIcon from 'src/images/icons/alert-modal.svg';
import ArrowLeft from 'src/images/icons/arrow-left.svg';
import ArrowDown from 'src/images/arrow-down.svg';
import ArrowUp from 'src/images/arrow-up.svg';

import {useChatbotVariables, useEditorState} from '../node-editor/reducer';

import sidePanelCss from 'src/components/modals/generic-sidepanel.css';
import css from './node-editor-v2.css';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {options} from 'amplitude-js';
import label from 'src/components/lib/label/label';
import {AttributeMetaData} from 'src/types/writeback';


export default function QuestionNode({
  onCancel,
  graph,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const {state, dispatch} = useChatbotStateContext();

  const [defaultNode, setDefaultNode] = React.useState<any>(null);
  const [presetQuestions, setPresetQuestions] = React.useState<any>();
  const [searchValue, setSearchValue] = React.useState('');
  const [smartSearchResults, setSmartSearchResults] = React.useState([]); //these are results that come from /questions/similarity api from search term, which should be displayed along with exact string match results

  React.useEffect(() => {
    api.get('chatbot/questions').then((res) => {
      const {questions} = res;
      setPresetQuestions(questions);
    });
  }, []);

  React.useEffect(() => {
    if (state.editingNode) {
      const editingNodeData = graph.nodes.find(
        ({id}) => id === state.editingNode,
      );
      if (editingNodeData) {
        setDefaultNode(editingNodeData);
      }
    }
  }, [state && state.editingNode]);

  const questionCategories = React.useMemo(
    () =>
      presetQuestions
        ? uniqBy(presetQuestions, 'intent_label').map(
            // @ts-expect-error - TS2339 - Property 'intent_label' does not exist on type '{ toString: () => string; charAt: (pos: number) => string; charCodeAt: (index: number) => number; concat: (...strings: string[]) => string; indexOf: (searchString: string, position?: number | undefined) => number; ... 46 more ...; [Symbol.iterator]: () => StringIterator<...>; }'.
            ({intent_label}) => intent_label,
          )
        : [],
    [presetQuestions],
  );

  const questionsByCategory = React.useMemo(
    () =>
      presetQuestions
        ? // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'question' implicitly has an 'any' type.
          presetQuestions.reduce<Record<string, any>>((result, question) => {
            result[question.intent_label] = result[question.intent_label]
              ? [...result[question.intent_label], question]
              : [question];
            return result;
          }, {})
        : {},
    [presetQuestions],
  );

  // @ts-expect-error - TS7006 - Parameter 'text' implicitly has an 'any' type.
  const checkQuestionClassifier = (text) => {
    api
      .post('chatbot/questions/batch/similarity', {questions: [text]})
      .then((res) => {
        const filteredSearchResults = res.result[0].predicted_labels.filter(
          // @ts-expect-error - TS7031 - Binding element 'score' implicitly has an 'any' type.
          ({score}) => score > QUESTION_SIMILARITY_THRESHOLD,
        );
        setSmartSearchResults(filteredSearchResults);
      })
      .catch(() => console.error('failed to check question similarity'));
  };

  const debouncedCheckQuestionClassifier = React.useCallback(
    debounce(checkQuestionClassifier, 1000),
    [],
  );

  // @ts-expect-error - TS7006 - Parameter 'questionData' implicitly has an 'any' type.
  const handleCreatePresetNode = (questionData) => {
    const {
      question,
      data_collected,
      id,
      response_type,
      data_value,
      validation_type,
      title,
    } = questionData;

    const newAttrs = {
      name: title ?? '',
      text_draft_json: createRawDraftContentState(question),
      preset_question_id: id,
      question_type: 'preset',
      response_type: validation_type ?? 'string',
      question_is_multiselect: response_type[0] === 'multiselect',
      derived_field_map:
        data_collected?.length > 0
          ? // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'result' implicitly has an 'any' type. | TS7006 - Parameter 'data' implicitly has an 'any' type.
            data_collected.reduce<Record<string, any>>((result, data) => {
              result[data.slot] = {
                editable: true,
                exported: true,
                field_label: data.label,
                field_name: data.slot,
              };
              return result;
            }, {})
          : null,
      answers: data_value
        ? // @ts-expect-error - TS7031 - Binding element 'label' implicitly has an 'any' type. | TS7031 - Binding element 'value' implicitly has an 'any' type.
          data_value.map(({label, value}) => ({
            disabled: false,
            label,
            value,
            id: nanoid(),
          }))
        : null,
    } as const;

    const newNode = defaultNode
      ? {...defaultNode, ...newAttrs}
      : createQuestionNode(newAttrs);
    setDefaultNode(newNode);
    return newNode;
  };

  if (!presetQuestions && !defaultNode?.type) {
    return <Loading />;
  }

  if (defaultNode?.type === 'question') {
    return (
      <QuestionEditor
        defaultNode={defaultNode}
        setDefaultNode={setDefaultNode}
        graph={graph}
        presetQuestions={presetQuestions ?? []}
        onCancel={onCancel}
        readOnly={readOnly}
        isSavingNode={isSavingNode}
        setIsSavingNode={setIsSavingNode}
        handleCreatePresetNode={handleCreatePresetNode}
      />
    );
  }

  return (
    <div className={css.root}>
      <QuestionHeader
        onCancel={onCancel}
        header={
          <Header className={sidePanelCss.title}>
            {t('New_Question', 'New Question')}
          </Header>
        }
      />
      <div className={css.section}>
        <SearchInput
          className={css.searchInput}
          value={searchValue}
          placeholder={`${t(
            'What_kind_of_question_do_you_want_to_ask',
            'What kind of question do you want to ask',
          )}?`}
          onChange={(val) => {
            setSearchValue(val);
            debouncedCheckQuestionClassifier(val);
          }}
        />
      </div>

      <div className={css.column}>
        {questionCategories.map((intentLabel) => {
          const questions = questionsByCategory[intentLabel] ?? [];

          return (
            <QuestionCategory
              intentLabel={intentLabel}
              questions={questions}
              searchValue={searchValue}
              smartSearchResults={smartSearchResults}
              handleCreatePresetNode={handleCreatePresetNode}
            />
          );
        })}
      </div>

      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="secondary"
          onClick={() => {
            setDefaultNode(createQuestionNode());
          }}
        >
          {t('Add_Custom_Question', 'Add Custom Question')}
        </Button>
      </div>
    </div>
  );
}

const QuestionCategory = ({
  // @ts-expect-error - TS7031 - Binding element 'intentLabel' implicitly has an 'any' type.
  intentLabel,
  // @ts-expect-error - TS7031 - Binding element 'questions' implicitly has an 'any' type.
  questions,
  // @ts-expect-error - TS7031 - Binding element 'searchValue' implicitly has an 'any' type.
  searchValue,
  // @ts-expect-error - TS7031 - Binding element 'smartSearchResults' implicitly has an 'any' type.
  smartSearchResults,
  // @ts-expect-error - TS7031 - Binding element 'handleCreatePresetNode' implicitly has an 'any' type.
  handleCreatePresetNode,
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const expand = searchValue || isExpanded;

  const searchValueTrim = searchValue.toLowerCase();
  const smartSearchMatches = smartSearchResults.map(
    // @ts-expect-error - TS7031 - Binding element 'nlu_entity_type' implicitly has an 'any' type. | TS7031 - Binding element 'intent' implicitly has an 'any' type.
    ({nlu_entity_type, intent}) => `${nlu_entity_type}${intent}`,
  ); //just to make filtering below faster

  const filteredQuestions = React.useMemo(
    () =>
      questions.filter(
        // @ts-expect-error - TS7031 - Binding element 'question' implicitly has an 'any' type. | TS7031 - Binding element 'data_collected' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type. | TS7031 - Binding element 'nlu_entity_type' implicitly has an 'any' type. | TS7031 - Binding element 'intent' implicitly has an 'any' type.
        ({question, data_collected, id, nlu_entity_type, intent}) => {
          const matchesQuestion = question
            .toLowerCase()
            .includes(searchValueTrim);
          // @ts-expect-error - TS7031 - Binding element 'label' implicitly has an 'any' type.
          const matchesSlots = data_collected?.some(({label}) =>
            label.toLowerCase().includes(searchValueTrim),
          );
          const matchesSmartSearchResult = smartSearchMatches.includes(
            `${nlu_entity_type}${intent}`,
          );

          return matchesQuestion || matchesSlots || matchesSmartSearchResult;
        },
      ),
    [searchValue, smartSearchMatches.join('')],
  );

  return (
    <div className={css.column} key={intentLabel}>
      <CollapsibleSection
        header={questions[0].intent_label}
        content={
          <div className={css.column}>
            {/* @ts-expect-error TS7006 */}
            {filteredQuestions.map((question) => (
              <div
                className={css.questionContainer}
                onClick={() => handleCreatePresetNode(question)}
                key={question.id}
              >
                <strong className={css.plusIcon}> +</strong>
                <div className={css.column}>
                  <div className={css.question}>{question.question}</div>
                  <div className={css.dataCollected}>
                    {question.data_collected
                      ? `${t(
                          'Data_type',
                          'Data type',
                        )}: ${question.data_collected
                          // @ts-expect-error - TS7031 - Binding element 'label' implicitly has an 'any' type.
                          .map(({label}) => label)
                          .join(', ')}`
                      : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
        showHeader={!searchValue}
        showContent={Boolean(searchValue)}
        showContentByDefault={false}
      />
    </div>
  );
};

function QuestionEditor({
  defaultNode,
  graph,
  presetQuestions,
  onCancel,
  setDefaultNode,
  readOnly,
  isSavingNode,
  setIsSavingNode,
  handleCreatePresetNode,
}: {
  defaultNode: QuestionNodeType;
  graph: Graph;
  onCancel: () => unknown;
  presetQuestions: PresetQuestion[];
  setDefaultNode: (arg1?: QuestionNodeType | null | undefined) => void;
  readOnly: boolean;
  isSavingNode: boolean;
  setIsSavingNode: (arg1: boolean) => unknown;
  handleCreatePresetNode: (arg1: PresetQuestion) => QuestionNodeType;
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const richTextEditorEnabled = useReleaseFlag('chatbotFlowRichEditor');
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );

  const isChatbotLiveFlowEnabled = useChatbotLiveFlowEditEnabled();
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const {state, dispatch} = useChatbotStateContext();
  const [editorState, editorDispatch] = useEditorState<QuestionNodeType>({
    defaultNode,
  });
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const {node} = editorState;

  const setNode = (payload: QuestionNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const questionsById = React.useMemo(
    () => presetQuestions && keyBy(presetQuestions, 'id'),
    [presetQuestions],
  );

  const presetQuestionId =
    // @ts-expect-error - TS2339 - Property 'additional_configs_json' does not exist on type 'QuestionNode'.
    node.preset_question_id ?? node.additional_configs_json?.preset_question_id;

  const [isEditingQuestion, setIsEditingQuestion] = React.useState(
    !presetQuestionId,
  );

  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  const [isQuestionRelevant, setIsQuestionRelevant] = React.useState(true);
  const [questionSuggestions, setQuestionSuggestions] = React.useState([]);

  const [openSuggestion, setOpenSuggestion] = React.useState(true);

  const [sortButtonsAscending, setSortButtonsAscending] = React.useState(true);

  const closeSuggestion = React.useMemo(
    () => () => setOpenSuggestion(false),
    [],
  );
  React.useEffect(() => {
    window.addEventListener('click', closeSuggestion);
    return () => {
      window.removeEventListener('click', closeSuggestion);
    };
  }, []);

  const entityType = graph.anchor_entity_type;

  const presetQuestion = presetQuestionId
    ? questionsById[presetQuestionId]
    : null;

  React.useEffect(() => {
    // Preset question nodes with a single data collected field were created with writeback_field_id and no derived_field_map before our switch to using derived_field_map for all preset questions. This update here ensures that going forward, we are only using derived_field_map for preset question writeback fields. New nodes created already follows this pattern, so this is just to fix bad data from before the switch.
    if (
      node.question_type === 'preset' &&
      !node.derived_field_map &&
      presetQuestion
    ) {
      const {data_collected} = presetQuestion;

      if (data_collected.length > 0) {
        const data = data_collected[0];

        setNode({
          ...node,
          writeback_field_id: null,
          derived_field_map: {
            [data.slot]: {
              editable: true,
              exported: true,
              field_label: data.label,
              field_name: data.slot,
              writeback_field_id: node.writeback_field_id ?? null,
            },
          },
        });
      }
    }
  }, [presetQuestion]);

  const content = node.text_draft_json && convertFromRaw(node.text_draft_json);

  const questionText = content && content.getPlainText();

  const responseTypeOptions = [
    {value: 'string', label: t('Free_Form', 'Free Form')},
    {value: 'boolean', label: t('YesNo', 'Yes/No')},
    {
      value: 'buttons',
      label: t('Singleselect_Buttons', 'Single-select Buttons'),
    },
    {
      value: 'multiselect',
      label: t('Multiselect_Buttons', 'Multi-select Buttons'),
    },
  ];

  const responseOptions = presetQuestion
    ? responseTypeOptions.filter((option) =>
        presetQuestion.response_type.includes(option.value),
      )
    : responseTypeOptions;
  const responseTypeValue =
    node.response_type === 'boolean'
      ? 'boolean'
      : node.answers
      ? node.question_is_multiselect
        ? 'multiselect'
        : 'buttons'
      : 'string';

  const checkQuestionClassifier = () => {
    if (questionText) {
      api
        .post('chatbot/questions/batch/similarity', {questions: [questionText]})
        .then((res) => {
          if (presetQuestion) {
            const topMatch = res.result[0].predicted_labels[0];
            const doesCustomLanguageMatch =
              presetQuestion.nlu_entity_type === topMatch.nlu_entity_type &&
              presetQuestion.intent === topMatch.intent;
            setIsQuestionRelevant(doesCustomLanguageMatch);
          } else {
            const filteredResults = res.result[0].predicted_labels.filter(
              // @ts-expect-error - TS7031 - Binding element 'score' implicitly has an 'any' type.
              ({score}) => score > QUESTION_SIMILARITY_THRESHOLD,
            );
            if (filteredResults.length > 0) {
              setQuestionSuggestions(filteredResults);
              setOpenSuggestion(true);
            }
          }
        })
        .catch(() => console.error('failed to check question similarity'));
    }
  };

  const presetQuestionSuggestions = React.useMemo(
    () =>
      questionSuggestions
        .map(({intent, nlu_entity_type}) => {
          return presetQuestions.find(
            (presetQuestion) =>
              presetQuestion.intent === intent &&
              presetQuestion.nlu_entity_type === nlu_entity_type,
          );
        })
        .filter(Boolean),
    [questionSuggestions, presetQuestions],
  );

  React.useEffect(() => {
    if (!presetQuestion) {
      checkQuestionClassifier();
    } else if (questionText === presetQuestion.question) {
      setIsQuestionRelevant(true);
    } else {
      checkQuestionClassifier();
    }
  }, [questionText, presetQuestion]);

  React.useEffect(() => {
    if (
      !presetQuestion &&
      state.convertingToPreset &&
      presetQuestionSuggestions.length === 1
    ) {
      // @ts-expect-error - TS2345 - Argument of type 'PresetQuestion | undefined' is not assignable to parameter of type 'PresetQuestion'.
      const presetNode = handleCreatePresetNode(presetQuestionSuggestions[0]);
      setNode(presetNode);
      setIsEditingQuestion(false);
    }
  }, [presetQuestionSuggestions]);

  const multipleChoiceOptions = presetQuestion
    ? presetQuestion.data_value?.map(
        (attrs: {label: string; value: string}) => ({
          ...attrs,
          id: attrs.value,
          disabled: false,
        }),
      )
    : node.answers;

  const editableGraph = createEditableGraph(graph);
  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);
  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
    false,
    false,
    allowMeetingNodeVariables,
  );
  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const writebackAttributes = useWritebackMetadataV2(entityType);

  const {getWbFieldId} = useOWBFields();

  const {writeback_field_id} = node;
  const attributeWritebackInfo =
    writeback_field_id && writebackAttributes
      ? writebackAttributes.find(
          (metadata) => getWbFieldId(metadata) === writeback_field_id,
        )
      : null;

  const writebackDataType = attributeWritebackInfo?.data_type;

  const writebackResponseTypeOptions = [
    {
      value: 'buttons',
      label: t('Singleselect', 'Single-select'),
    },
    {
      value: 'multiselect',
      label: t('Multiselect', 'Multi-select'),
    },
  ].filter((opt) => {
    if (attributeWritebackInfo != null) {
      if (
        !attributeWritebackInfo.allow_select_multiple &&
        opt.value === 'multiselect'
      ) {
        return false;
      }
    }
    return true;
  });

  const isCompositeQuestion = Number(presetQuestion?.data_collected.length) > 1;

  let hasFixedDataType;
  const dataTypeOptions = getDataTypeOptions(t);
  let validationTypeOptions = dataTypeOptions;

  if (!useReleaseFlag('chatbotAddressValidationEnabled')) {
    validationTypeOptions = validationTypeOptions.filter(
      (opt) => opt.value !== 'address',
    );
  }

  if (writebackDataType && writebackDataType !== 'string') {
    const allowedValidationTypes =
      writebackDataTypesToValidationTypes.get(writebackDataType);
    if (allowedValidationTypes) {
      hasFixedDataType = false;
      validationTypeOptions = dataTypeOptions.filter((dataTypeOption) =>
        allowedValidationTypes.includes(dataTypeOption.value),
      );
    } else {
      hasFixedDataType = true;
    }
  } else {
    hasFixedDataType = false;
  }

  const handleAddressChange =
    (addressFieldName: string) =>
    (attributeMetadata: AttributeMetaData | null | undefined) => {
      const writebackFieldId = getWbFieldId(attributeMetadata);
      editorDispatch({
        type: 'set_address_writeback',
        payload: addressFieldName,
        metadata: {
          writebackFieldId,
        },
      });
    };

  const [atsVariablePresetError, setAtsVariablePresetError] =
    React.useState(false);

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const singleSelectTypeChangeDisabled =
    responseTypeValue === 'buttons' &&
    getChildEdges(editableGraph, defaultNode.id).length > 1;

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const derivedAddressFields = getDerivedAddressFields(t);
  const dynamicLabels = useChatbotVariables(entityType, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  const isUsingDiscoverAttribute = getIsUsingDiscoverAttribute(
    node.text_draft_json,
  );

  if (state.convertingToPreset && !presetQuestionSuggestions?.length) {
    return <Loading />;
  }
  return (
    <div className={css.questionNodeContainer}>
      <QuestionHeader
        onCancel={onCancel}
        header={
          <Header className={sidePanelCss.title}>
            {state.insertingNode && (
              <ArrowLeft
                className={css.navBack}
                onClick={() => {
                  setDefaultNode(null);
                }}
              />
            )}
            <div className={css.headerRow}>
              <div>{node.name}</div>
              {node.preset_question_id && (
                // @ts-expect-error - TS2769 - No overload matches this call.
                <Chip iconName="" semantic="information" size="medium">{`${t(
                  'Preset',
                  'Preset',
                )}  `}</Chip>
              )}
            </div>
          </Header>
        }
      />
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
          disabled={isFieldReadOnly('name')}
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.section}>
        <div className={css.inputLabel}>
          {presetQuestion
            ? `${t('Preset_Question', 'Preset Question')}`
            : `${t('Question', 'Question')}*`}
          {!presetQuestion && (
            <Link
              color="clickable"
              underline={false}
              onClick={(e) => {
                e.stopPropagation();
                setOpenSuggestion(true);
              }}
            >
              <Icon
                color="clickable"
                name="sparkles"
                size="medium"
                type="regular"
              />
              {t('Suggest_Preset_Question', 'Suggest Preset Question')}
            </Link>
          )}
        </div>
        {isEditingQuestion && !isFieldReadOnly('text_draft_json') ? (
          <>
            {richTextEditorEnabled ? (
              <RichEditorWithWarning
                warningMessage={
                  isUsingDiscoverAttribute
                    ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                    : ''
                }
                defaultValue={node.text_draft_json}
                onChange={(text_draft_json) => {
                  setNode({...node, text_draft_json});
                }}
                editorHeight="200px"
                includeFormatting={Boolean(richTextEditorFormattingEnabled)}
                includeHyperlink={!chatbotNewHyperlinkModalEnabled}
                includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
                includeGif
                // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
                nodeVariables={mappedNodeVariables}
                atsVariables={
                  graph.use_case === 'sourcing' ? undefined : dynamicLabels
                }
                readOnly={isFieldReadOnly('text_draft_json')}
                entityType={graph.anchor_entity_type}
                prepareContentOnLoad={prepareContentOnLoad}
                atsTooltip={
                  hasAncestorJobMatchQuestionNode
                    ? `${t(
                        'ATS_External_variables',
                        'ATS & External variables',
                      )}`
                    : `${t('ATS_variables', 'ATS variables')}`
                }
                enableDraftJSONCopy
              />
            ) : (
              <MessageDraftEditor
                placeholder={`${t('Enter_a_question', 'Enter a question')}...`}
                defaultValue={node.text_draft_json}
                onChange={(text_draft_json) =>
                  setNode({...node, text_draft_json})
                }
                entityType={entityType}
                isFirstSmsMessage={false}
                extraCharacterCount={
                  node.answers
                    ? node.answers.reduce(
                        (length, answer) =>
                          length + 'A: \n'.length + answer.label.length,
                        0,
                      )
                    : 0
                }
                label=""
                useCase={graph.use_case}
                nodeVariables={nodeVariables}
                readOnly={isFieldReadOnly('text_draft_json')}
                onBlur={() => {
                  if (presetQuestion && hasText(node.text_draft_json)) {
                    setIsEditingQuestion(false);
                  }
                }}
                showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
                showExternalVariables={Boolean(
                  graph.flow_config.api_bot_enabled,
                )}
                warningMessage={
                  isUsingDiscoverAttribute
                    ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                    : ''
                }
              />
            )}
            {!presetQuestion &&
              presetQuestionSuggestions.length > 0 &&
              openSuggestion && (
                <div
                  className={css.questionSuggestions}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className={css.questionSuggestionTitle}>
                    {t(
                      'Suggestions_from_our_Question_Library',
                      'Suggestions from our Question Library',
                    )}
                    <MouseTip content="These questions were designed by a linguistic expert and are trained with improved Natural Language Processing data to provide a seamless experience for respondents. Selecting one of these options will convert this question into a preset question.">
                      <InfoIcon className={css.infoIcon} />
                    </MouseTip>
                  </div>
                  {presetQuestionSuggestions.map((suggestion) => (
                    <div
                      className={css.questionSuggestionRow}
                      onClick={() => {
                        // @ts-expect-error - TS2345 - Argument of type 'PresetQuestion | undefined' is not assignable to parameter of type 'PresetQuestion'.
                        const presetNode = handleCreatePresetNode(suggestion);
                        setNode(presetNode);
                        setIsEditingQuestion(false);
                      }}
                    >
                      {/* @ts-expect-error - TS18048 - 'suggestion' is possibly 'undefined'. */}
                      {suggestion.question}
                    </div>
                  ))}
                </div>
              )}
          </>
        ) : (
          <div className={css.questionDisplay}>
            <div className={css.questionDisplayText}>{questionText}</div>
            <div>
              {!isQuestionRelevant && (
                <WarningIcon className={css.warningIcon} />
              )}
              <div
                className={css.editQuestion}
                onClick={() => setIsEditingQuestion(true)}
              >
                <EditIcon />
              </div>
            </div>
          </div>
        )}
        {!isQuestionRelevant && isEditingQuestion && (
          <div className={css.warning}>
            <WarningIcon className={css.warningIcon} />
            {`${t(
              'Custom_language_does_not_match_this_preset_question_type',
              'Custom language does not match this preset question type',
            )}.`}
          </div>
        )}
        {error?.text_draft_json?.errors?.includes('EMPTY') && (
          <div className={css.errorText}>{`${t(
            'Message_cannot_be_empty',
            'Message cannot be empty',
          )}.`}</div>
        )}
      </div>
      <CollapsibleSection
        header={t('Manage_Response_Type', 'Manage Response Type')}
        content={
          <div className={classify(css.section, css.padded)}>
            {node.preset_question_id && responseOptions.length === 1 && (
              <p className={css.description}>{`${t(
                'This_preset_question_collects_data_that_can_only_be_accepted_as_a_free_form_answer_This_cannot_be_changed',
                'This preset question collects data that can only be accepted as a free form answer. This cannot be changed',
              )}.`}</p>
            )}

            {/*  @ts-expect-error - TS2739 - Type '{ label: string; contextLabel: string; value: { value: string; label: string; } | undefined; disabled: boolean; options: { value: string; label: string; }[]; onChange: (selected: { value: string; label: string; } | null | undefined) => void; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: string; label: string; }>': resolveKey, resolveLabel */}
            <Select
              label={t('Response_Type', 'Response Type')}
              contextLabel={
                responseOptions.length === 1 ? t('Locked', 'Locked') : ''
              }
              value={responseTypeOptions.find(
                ({value}) => value === responseTypeValue,
              )}
              disabled={
                responseOptions.length === 1 ||
                singleSelectTypeChangeDisabled ||
                readOnly
              }
              options={responseOptions}
              onChange={(selected) => {
                if (selected?.value) {
                  editorDispatch({
                    type: 'change_buttons_type',
                    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type '"string" | "boolean" | "buttons" | "multiselect" | null'.
                    payload: selected.value,
                  });
                }
              }}
            />
            {singleSelectTypeChangeDisabled && (
              <p className={css.description}>{`${t(
                'To_change_the_response_type_from_singleselect_buttons_you_must_remove_all_child_branches_from_this_node',
                'To change the response type from single-select buttons, you must remove all child branches from this node',
              )}.`}</p>
            )}
          </div>
        }
      />
      <CollapsibleSection
        header={t('Manage_Data', 'Manage Data')}
        content={
          <div className={classify(css.section, css.padded)}>
            {presetQuestion && (
              <p className={css.description}>{`${t(
                'This_preset_question_collects_specific_data_that_can_be_recorded_into_separate_ATS_fields',
                'This preset question collects specific data that can be recorded into separate ATS fields',
              )}:`}</p>
            )}
            {node.response_type !== 'address' && (
              <>
                {!presetQuestion && (
                  <p className={css.description}>{`${t(
                    'ATS_writebacks_allow_you_to_designate_which_ATS_field_to_record_the_collected_data',
                    'ATS writebacks allow you to designate which ATS field to record the collected data',
                  )}.`}</p>
                )}
                {(presetQuestion?.data_value || !presetQuestion) && (
                  <>
                    <div className={css.inputLabel}>{`${t(
                      'ATS_Writeback_Field',
                      'ATS Writeback Field',
                    )} `}</div>
                    {writebackAttributes ? (
                      <LoadedVariablePicker
                        entityType={entityType}
                        value={node.writeback_field_id}
                        attributeMetadata={
                          node.answers && node.response_type !== 'boolean'
                            ? writebackAttributes.filter(({possible_answers}) =>
                                Boolean(possible_answers),
                              )
                            : writebackAttributes
                        }
                        onChange={(attributeMetadata) => {
                          const writebackFieldId =
                            getWbFieldId(attributeMetadata);
                          editorDispatch({
                            type: 'set_writeback',
                            payload: writebackFieldId,
                            metadata: {
                              previous: attributeWritebackInfo,
                              next: attributeMetadata,
                            },
                          });
                        }}
                        // @ts-expect-error - TS2367 - This comparison appears to be unintentional because the types '"string" | "number" | "boolean" | "email" | "currency" | "date" | "start date" | "end date" | "money" | "phone number" | "zipcode"' and '"address"' have no overlap.
                        readOnly={readOnly || node.response_type === 'address'}
                      />
                    ) : (
                      <Loading />
                    )}
                  </>
                )}
              </>
            )}
            {!presetQuestion && (
              <>
                {!node.answers && (
                  <>
                    <p className={css.description}>
                      {`${t(
                        'Validation_checks_if_the_collected_data_follows_a_specific_format_Ex_If_collecting_a_zipcode_the_zipcode_validation_type_will_check_to_see_if_the_response_provided_is_a_valid_zipcode',
                        'Validation checks if the collected data follows a specific format. Ex: If collecting a zipcode, the “zipcode” validation type will check to see if the response provided is a valid zipcode',
                      )}.`}{' '}
                    </p>
                    <div className={css.column}>
                      <div className={css.inputLabel}>{`${t(
                        'Validation',
                        'Validation',
                      )} `}</div>
                      {/*  @ts-expect-error - TS2739 - Type '{ value: { value: ResponseType; label: string; } | undefined; options: { value: ResponseType; label: string; }[]; onChange: (option: { value: ResponseType; label: string; } | null | undefined) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: ResponseType; label: string; }>': resolveKey, resolveLabel */}
                      <Select
                        value={validationTypeOptions.find(
                          ({value}) => value === node.response_type,
                        )}
                        options={validationTypeOptions}
                        onChange={(option) => {
                          if (option?.value) {
                            setNode({
                              ...node,
                              derived_field_map:
                                option.value === 'address'
                                  ? node.derived_field_map
                                  : null, //remove derived field map when validation type is not address
                              response_type: option.value,
                            });
                          }
                        }}
                        disabled={readOnly || hasFixedDataType}
                      />
                    </div>
                  </>
                )}

                {!presetQuestion &&
                  node.response_type === 'address' &&
                  derivedAddressFields.map(
                    ([fieldValue, fieldName]: [any, any]) => {
                      return (
                        <div className={css.column} key={fieldValue}>
                          <div className={css.inputLabel}>{fieldName}</div>
                          <LoadedVariablePicker
                            entityType={entityType}
                            value={
                              node.derived_field_map?.[fieldValue]
                                ?.writeback_field_id
                            }
                            attributeMetadata={writebackAttributes}
                            onChange={handleAddressChange(fieldValue)}
                            readOnly={readOnly}
                          />
                        </div>
                      );
                    },
                  )}
              </>
            )}
            <div className={css.buttonControls}>
              {node.answers && !presetQuestion && node.writeback_field_id && (
                <div>
                  <Checkbox
                    className={css.checkbox}
                    checked={
                      node.answers.every(({disabled}) => !disabled)
                        ? 'true'
                        : 'false'
                    }
                    onChange={(checked) => {
                      const disabled = checked === 'true' ? false : true;

                      editorDispatch({
                        type: 'update',
                        payload: {
                          answers: node.answers?.map((answer: Answer) => ({
                            ...answer,
                            disabled,
                          })),
                        },
                      });
                    }}
                  />{' '}
                  {t('Enable_all', 'Enable all')}
                </div>
              )}
              {node.answers && !presetQuestion && (
                <div
                  className={css.buttonOrder}
                  onClick={() => {
                    const newAnswers = sortBy(
                      [...(node.answers ?? [])],
                      ['label'],
                    );
                    if (sortButtonsAscending === false) {
                      reverse(newAnswers);
                    }
                    editorDispatch({
                      type: 'update',
                      payload: {
                        answers: newAnswers,
                      },
                    });
                    setSortButtonsAscending(!sortButtonsAscending);
                  }}
                >{`${t('Button_Order', 'Button Order')}: ${
                  sortButtonsAscending ? '↓' : '↑'
                }`}</div>
              )}
            </div>
            {['buttons', 'multiselect', 'boolean'].includes(
              responseTypeValue,
            ) &&
              multipleChoiceOptions?.map(
                ({label, value, id, disabled}, index: number) => {
                  const findNode = (answer: Answer) =>
                    presetQuestion ? answer.value === value : answer.id === id;
                  const answerData = node.answers?.find(findNode);
                  const answerIndex = node.answers?.findIndex(findNode);

                  if (
                    !answerData ||
                    answerIndex === undefined ||
                    answerIndex === null
                  ) {
                    return null;
                  }

                  return (
                    <div
                      className={presetQuestion ? css.column : css.optionRow}
                      key={String(id)}
                    >
                      {!presetQuestion && node.writeback_field_id && (
                        <Checkbox
                          className={css.checkbox}
                          checked={disabled ? 'false' : 'true'}
                          onChange={() => {
                            const newAnswers = [...(node.answers ?? [])];
                            newAnswers[index] = {
                              ...newAnswers[index],
                              disabled: !disabled,
                            };
                            editorDispatch({
                              type: 'update',
                              payload: {
                                answers: newAnswers,
                              },
                            });
                          }}
                        />
                      )}
                      <div>
                        {presetQuestion && (
                          <label className={css.inputContainer}>
                            <Input
                              label={t('Bot_Value', 'Bot Value')}
                              value={label}
                              disabled
                            />
                          </label>
                        )}
                        {node.writeback_field_id && presetQuestion && (
                          <div className={css.inputContainer}>
                            <div className={css.inputLabel}>
                              {t('Map_to_ATS_Value', 'Map to ATS Value')}
                            </div>
                            {/*  @ts-expect-error - TS2739 - Type '{ options: PossibleAnswer[]; value: PossibleAnswer | undefined; onChange: (selected: PossibleAnswer | null | undefined) => void; }' is missing the following properties from type 'SenseTypeaheadProps<PossibleAnswer>': resolveKey, resolveLabel */}
                            <Select
                              options={
                                attributeWritebackInfo?.possible_answers ?? []
                              }
                              value={attributeWritebackInfo?.possible_answers?.find(
                                ({value}) =>
                                  value === answerData.writeback_field_value,
                              )}
                              onChange={(selected) => {
                                if (selected?.value) {
                                  const newAnswers = [...(node.answers ?? [])];
                                  newAnswers[index] = {
                                    ...newAnswers[index],
                                    writeback_field_value: selected.value,
                                  };
                                  setNode({
                                    ...node,
                                    answers: newAnswers,
                                  });
                                }
                              }}
                            />
                          </div>
                        )}
                        {node.writeback_field_id && !presetQuestion && (
                          <div className={css.inputContainer}>
                            <Input
                              disabled
                              value={String(value)}
                              label={t('ATS_Value', 'ATS Value')}
                            />
                          </div>
                        )}
                      </div>
                      <label className={css.inputContainer}>
                        <div>
                          <Input
                            label={t(
                              'ButtonDropdown_Label',
                              'Button/Dropdown Label',
                            )}
                            value={answerData.label}
                            onChange={(event) => {
                              const newAnswers = node.answers
                                ? [...node.answers]
                                : [];

                              if (
                                Array.isArray(newAnswers) &&
                                answerIndex !== -1
                              ) {
                                const keepAnswerValue =
                                  presetQuestion ||
                                  node.response_type === 'boolean' ||
                                  node.writeback_field_id;
                                if (event.currentTarget.value) {
                                  newAnswers[answerIndex] = {
                                    ...answerData,
                                    label: event.currentTarget.value,
                                    value: keepAnswerValue
                                      ? answerData.value
                                      : event.currentTarget.value,
                                    disabled: presetQuestion
                                      ? false
                                      : answerData.disabled,
                                  };
                                } else {
                                  newAnswers[answerIndex] = {
                                    ...answerData,
                                    label: event.currentTarget.value,
                                    value: keepAnswerValue
                                      ? answerData.value
                                      : event.currentTarget.value,
                                    disabled: presetQuestion
                                      ? true
                                      : answerData.disabled,
                                  };
                                }

                                return setNode({...node, answers: newAnswers});
                              }
                            }}
                            disabled={readOnly}
                          />

                          {answerData.label && (
                            <MouseTip
                              content={t(
                                'Clear_label_to_hide_this_response_option_from_respondants_Respondants_can_still_type_this_response_value_but_will_not_be_able_to_select_it_from_the_dropdown_or_button_options',
                                'Clear label to hide this response option from respondants. Respondants can still type this response value, but will not be able to select it from the dropdown or button options.',
                              )}
                              fixedTo="top"
                              arrowPosition="end"
                            >
                              <div
                                className={css.clearIcon}
                                onClick={() => {
                                  const newAnswers = node.answers
                                    ? [...node.answers]
                                    : [];

                                  if (
                                    Array.isArray(newAnswers) &&
                                    answerIndex !== -1
                                  ) {
                                    const data = presetQuestion
                                      ? {
                                          value: answerData.value,
                                          disabled: true,
                                        }
                                      : {value: ''};
                                    newAnswers[answerIndex] = {
                                      ...answerData,
                                      label: '',
                                      ...data,
                                    };
                                  }
                                  return setNode({
                                    ...node,
                                    answers: newAnswers,
                                  });
                                }}
                              >
                                <ClearIcon />
                              </div>
                            </MouseTip>
                          )}
                        </div>

                        {!answerData.label && (
                          <div className={css.warning}>
                            <WarningIcon className={css.warningIcon} />
                            {`${t(
                              'If_left_blank_this_button_will_not_display_as_an_option',
                              'If left blank, this button will not display as an option',
                            )}.`}
                          </div>
                        )}
                      </label>

                      {!presetQuestion &&
                        Number(node.answers?.length) > 1 &&
                        node.response_type !== 'boolean' &&
                        !node.writeback_field_id && (
                          <div
                            className={css.removeOption}
                            onClick={() =>
                              editorDispatch({
                                type: 'remove_button',
                                payload: id,
                              })
                            }
                          >
                            {t('x', 'x')}
                          </div>
                        )}
                    </div>
                  );
                },
              )}

            {error?.answers?.errors?.includes('TOO_MANY') && (
              <div className={css.errorText}>{`${t(
                'Too_many_answers_Try_disabling_some',
                'Too many answers. Try disabling some',
              )}.`}</div>
            )}
            {error?.answers?.errors?.includes('DUPLICATE') && (
              <div className={css.errorText}>
                {t(
                  'Buttons_must_have_unique_labels',
                  'Buttons must have unique labels',
                )}
              </div>
            )}
            {!presetQuestion &&
              !node.writeback_field_id &&
              node.answers &&
              node.response_type !== 'boolean' && (
                <Button
                  size="small"
                  type="secondary"
                  onClick={() =>
                    editorDispatch({
                      type: 'add_button',
                    })
                  }
                  className={css.addOptionButton}
                >
                  {t('Add_button', 'Add button')}
                </Button>
              )}

            {presetQuestion &&
              !presetQuestion.data_value &&
              presetQuestion.data_collected.map(({label, slot}) => {
                return (
                  <>
                    <div key={slot}>
                      <label className={css.inputContainer}>
                        <div className={css.inputLabel}>
                          {t('Data_Collected', 'Data Collected')}
                        </div>
                        <Input
                          placeholder="select variable"
                          value={label}
                          disabled
                        />
                      </label>
                      <label className={css.inputContainer}>
                        <div className={css.inputLabel}>
                          {t('ATS_Writeback_Field', 'ATS Writeback Field')}
                        </div>
                        <LoadedVariablePicker
                          entityType={entityType}
                          value={
                            node.derived_field_map?.[slot].writeback_field_id ??
                            node.writeback_field_id
                          }
                          attributeMetadata={writebackAttributes}
                          onChange={(attributeMetadata) => {
                            if (
                              attributeMetadata?.possible_answers &&
                              disableFixedValueATSVars.includes(
                                presetQuestionId,
                              )
                            ) {
                              setAtsVariablePresetError(true);
                            } else {
                              setAtsVariablePresetError(false);
                            }

                            const writebackFieldId =
                              getWbFieldId(attributeMetadata);

                            editorDispatch({
                              type: 'set_address_writeback',
                              payload: slot,
                              metadata: {
                                writebackFieldId,
                              },
                            });
                          }}
                          readOnly={readOnly}
                        />
                      </label>
                    </div>
                    {atsVariablePresetError && (
                      <div className={css.errorText}>{`${t(
                        'Sorry_preset_questions_cannot_write_back_to_this_particular_ATS_field_Please_use_custom_questions_and_add_the_desired_writeback_ATS_field',
                        'Sorry, preset questions cannot write back to this particular ATS field. Please use custom questions and add the desired writeback ATS field',
                      )}.`}</div>
                    )}
                  </>
                );
              })}
          </div>
        }
      />
      <CollapsibleSection
        header={t(
          'Exception_Transition_Message_optional',
          'Exception & Transition Message (optional)',
        )}
        content={
          <div className={classify(css.section, css.padded)}>
            <div className={css.column}>
              <div className={css.inputLabel}>{`${t(
                'When_response_is_invalid_show_this_message',
                'When response is invalid, show this message',
              )}:`}</div>
              <Textarea
                placeholder={`${t(
                  'Sorry_I_didn\'t_get_that',
                  'Sorry, I didn\'t get that',
                )}.`}
                value={node.question_error_message ?? ''}
                onChange={(e) =>
                  setNode({...node, question_error_message: e.target.value})
                }
                disabled={isFieldReadOnly('question_error_message')}
              />
            </div>

            <div className={css.column}>
              <div className={css.inputLabel}>{`${t(
                'When_moving_on_to_the_next_node_show_this_message',
                'When moving on to the next node, show this message',
              )}:`}</div>
              <Textarea
                value={node.transition_message}
                onChange={(e) =>
                  setNode({
                    ...node,
                    transition_message: e.target.value,
                  })
                }
                disabled={isFieldReadOnly('transition_message')}
              />
            </div>
          </div>
        }
      />
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={
            atsVariablePresetError || (hasSubmitted && !!validation) || readOnly
              ? !isChatbotLiveFlowEnabled
              : readOnly
          }
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}

// @ts-expect-error - TS7031 - Binding element 'onCancel' implicitly has an 'any' type. | TS7031 - Binding element 'header' implicitly has an 'any' type.
const QuestionHeader = ({onCancel, header}) => (
  <div className={classify(sidePanelCss.headerContainer, css.header)}>
    {header}
    <div
      className={sidePanelCss.closeIconContainer}
      onClick={() => {
        onCancel && onCancel();
      }}
    >
      <CloseIcon className={sidePanelCss.closeIcon} />
    </div>
  </div>
);

const CollapsibleSection = ({
  header,
  content,
  showHeader = true,
  showContent,
  showContentByDefault = true,
}: {
  header: string;
  content: React.ReactNode;
  showHeader?: boolean;
  showContent?: boolean;
  showContentByDefault?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = React.useState(showContentByDefault);

  const expandContent =
    showContent === undefined ? isExpanded : showContent || isExpanded;
  return (
    <>
      {showHeader && (
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className={css.collapsibleHeader}
        >
          {header}
          {isExpanded ? (
            <ArrowUp className={css.chevron} />
          ) : (
            <ArrowDown className={css.chevron} />
          )}
        </div>
      )}
      {expandContent && content}
    </>
  );
};

// Question ids for questions that does not allow for multiple choice ATS variables
const disableFixedValueATSVars = [68, 54];
